import React, { useState } from "react";
import {
  Box,
  Paper,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  MenuItem,
  IconButton,
  Button,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { formatNumber } from "../../helpers/formaters";

const uid = () => Math.random().toString(36).slice(2);

export default function FormasPagamentoTable({
  value,
  onChange,
  textFieldStyles,
  tipoFormaPagamentoList = [],
  tipoFormaPagamentoListAtivos = [],
  bancosList = [],
  formatNumber,
  headerColor = "#669966",
  isEditing = false,
}) {
  const rows = value?.length
    ? value
    : [
        {
          id: uid(),
          tipo: "",
          descricao: "",
          dataConciliacao: "",
          valor: "",
          banco: "",
          agencia: "",
          conta: "",
          nroCheque: "",
          tipoTitulo: "",
        },
      ];

  const setRows = (updater) => {
    const next = typeof updater === "function" ? updater(rows) : updater;
    onChange?.(next);
  };

  const setCell = (rowId, key, value) => {
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, [key]: value } : r)),
    );
  };

  const addRow = () => {
    if (isEditing) return;
    setRows((prev) => [
      ...prev,
      {
        id: uid(),
        tipo: "",
        descricao: "",
        dataConciliacao: "",
        valor: "",
        banco: "",
        agencia: "",
        conta: "",
        nroCheque: "",
        tipoTitulo: "",
      },
    ]);
  };

  const removeRow = (rowId) => {
    if (isEditing) return;
    setRows((prev) =>
      prev.length === 1 ? prev : prev.filter((r) => r.id !== rowId),
    );
  };

  return (
    <Grid size={{ xs: 12 }}>
      <Paper sx={{ borderRadius: 1, overflow: "hidden" }} elevation={1}>
        <Box
          sx={{
            px: 2,
            py: 0.5,
            bgcolor: headerColor,
            color: "white",
            fontWeight: 600,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
            Formas de pagamento
          </Typography>

          <Button
            onClick={addRow}
            size="small"
            color="inherit"
            startIcon={<AddCircleOutlineIcon />}
          />
        </Box>

        <Box sx={{ px: 1, pt: 1, pb: 1.25 }}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              overflowX: "auto",
            }}
          >
            <Table
              size="small"
              sx={{ tableLayout: "fixed", width: "max-content" }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 12, fontWeight: 700, width: 180 }}>
                    Tipo *
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 700, width: 180 }}>
                    Descrição *
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 700, width: 150 }}>
                    Dt. Conciliação *
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 700, width: 130 }}>
                    Valor *
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 700, width: 280 }}>
                    Banco *
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 700, width: 150 }}>
                    Agência *
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 700, width: 150 }}>
                    Conta *
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 700, width: 100 }}>
                    Nº Cheque
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontSize: 12, fontWeight: 700, width: 60 }}
                  >
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ py: 0.5 }}>
                      <TextField
                        sx={{
                          ...textFieldStyles,
                          "& .MuiInputBase-root": { fontSize: 12 },
                        }}
                        select
                        required
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                "& .MuiMenuItem-root": {
                                  fontSize: "11px",
                                  minHeight: "24px",
                                  paddingTop: "2px",
                                  paddingBottom: "2px",
                                },
                              },
                            },
                          },
                          renderValue: (value) => {
                            const f = (tipoFormaPagamentoList || []).find(
                              (x) => String(x.id) === String(value),
                            );
                            return f ? f.name : "";
                          },
                        }}
                        fullWidth
                        size="small"
                        color="success"
                        value={r.tipo}
                        required
                        disabled={isEditing}
                        onChange={(e) => {
                          const selectedId = e.target.value;

                          const itemSelecionado = tipoFormaPagamentoList.find(
                            (t) => String(t.id) === String(selectedId),
                          );

                          setRows((prev) =>
                            prev.map((row) =>
                              row.id === r.id
                                ? {
                                    ...row,
                                    tipo: selectedId,
                                    descricao: itemSelecionado?.name ?? "",
                                  }
                                : row,
                            ),
                          );
                        }}
                      >
                        {(tipoFormaPagamentoListAtivos || []).map(
                          (tipoFormaPagamento) => (
                            <MenuItem
                              key={tipoFormaPagamento.id}
                              value={tipoFormaPagamento.id}
                            >
                              {tipoFormaPagamento.name}
                            </MenuItem>
                          ),
                        )}
                      </TextField>
                    </TableCell>

                    <TableCell sx={{ py: 0.5 }}>
                      <TextField
                        sx={textFieldStyles}
                        fullWidth
                        disabled={isEditing}
                        size="small"
                        color="success"
                        value={r.descricao}
                        InputProps={{ readOnly: true }}
                      />
                    </TableCell>

                    <TableCell sx={{ py: 0.5 }}>
                      <TextField
                        sx={textFieldStyles}
                        fullWidth
                        size="small"
                        color="success"
                        type="date"
                        disabled={isEditing}
                        InputLabelProps={{ shrink: true }}
                        value={r.dataConciliacao}
                        required
                        onChange={(e) =>
                          setCell(r.id, "dataConciliacao", e.target.value)
                        }
                      />
                    </TableCell>

                    <TableCell sx={{ py: 0.5 }}>
                      <TextField
                        sx={textFieldStyles}
                        fullWidth
                        size="small"
                        disabled={isEditing}
                        color="success"
                        value={r.valor}
                        required
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{
                          ...textFieldStyles,
                          "& .MuiInputBase-root": {
                            bgcolor: "rgba(0, 0, 0, 0.14)",
                            cursor: "default",
                          },
                          "& .MuiInputLabel-root": {
                            color: "text.secondary",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "divider",
                          },
                          "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "divider",
                            },
                          "& input": {
                            fontSize: "13px",
                            caretColor: "transparent",
                          },
                        }}
                        onChange={(e) => setCell(r.id, "valor", e.target.value)}
                        onBlur={(e) =>
                          setCell(
                            r.id,
                            "valor",
                            formatNumber(e.target.value, true),
                          )
                        }
                      />
                    </TableCell>

                    <TableCell sx={{ py: 0.5 }}>
                      <TextField
                        sx={{
                          ...textFieldStyles,
                          "& .MuiInputBase-root": { fontSize: 12 },
                        }}
                        select
                        required
                        disabled={isEditing}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                "& .MuiMenuItem-root": {
                                  fontSize: "11px",
                                  minHeight: "24px",
                                  paddingTop: "2px",
                                  paddingBottom: "2px",
                                },
                              },
                            },
                          },
                        }}
                        fullWidth
                        size="small"
                        color="success"
                        value={r.banco}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const contaSelecionada = bancosList?.find(
                            (c) => String(c.id) === String(selectedId),
                          );
                          setRows((prev) =>
                            prev.map((row) =>
                              row.id === r.id
                                ? {
                                    ...row,
                                    banco: selectedId,
                                    agencia: contaSelecionada?.agencia ?? "",
                                    conta: contaSelecionada?.conta ?? "",
                                  }
                                : row,
                            ),
                          );
                        }}
                      >
                        {bancosList?.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>

                    <TableCell sx={{ py: 0.5 }}>
                      <TextField
                        disabled={isEditing}
                        fullWidth
                        sx={textFieldStyles}
                        required
                        color="success"
                        size="small"
                        value={r.agencia ?? ""}
                        InputProps={{ readOnly: true }}
                      />
                    </TableCell>

                    <TableCell sx={{ py: 0.5 }}>
                      <TextField
                        required
                        sx={textFieldStyles}
                        color="success"
                        disabled={isEditing}
                        fullWidth
                        size="small"
                        value={r.conta ?? ""}
                        InputProps={{ readOnly: true }}
                      />
                    </TableCell>

                    <TableCell sx={{ py: 0.5 }}>
                      <TextField
                        sx={textFieldStyles}
                        fullWidth
                        disabled={isEditing}
                        size="small"
                        color="success"
                        value={r.nroCheque}
                        onChange={(e) =>
                          setCell(r.id, "nroCheque", e.target.value)
                        }
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ py: 0.5 }}>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={isEditing}
                        onClick={() => removeRow(r.id)}
                        title="Remover linha"
                        disabled={rows.length === 1}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ mt: 0.75, fontSize: 11, color: "text.secondary" }}>
            Dica: role horizontalmente para ver todas as colunas.
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
}
